import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STAT_LABELS = {
  kills_per_set: "Kills per set", hit_pct: "Hitting %", assists_per_set: "Assists per set", aces_per_set: "Aces per set",
  digs_per_set: "Digs per set", blocks_per_set: "Blocks per set", rec_err_rate: "Reception error rate", setter_kills: "Setter kills per set",
  ops: "OPS", iso: "Isolated power", avg: "Batting average", bb_rate: "Walk rate", k_rate: "Strikeout rate",
  xbh_gap_rate: "Doubles + triples rate", sb_per_game: "Stolen bases per game", k_pct: "Strikeout %", bb_pct: "Walk %",
  era: "ERA", whip: "WHIP", ip: "Innings pitched",
  pts_40: "Points per 40", reb_40: "Rebounds per 40", orb_40: "Offensive rebounds per 40", ast_40: "Assists per 40",
  stl_40: "Steals per 40", blk_40: "Blocks per 40", fta_40: "Free-throw attempts per 40", unast_fgm_40: "Unassisted makes per 40",
  ts_pct: "True shooting %", tp_pct: "3-point %", ft_pct: "Free-throw %", rim_pct: "At-rim FG %", mid_pct: "Mid-range FG %",
  ast_to: "Assist-to-turnover ratio",
};
const AVG_STATS = ["hit_pct", "ops", "iso", "avg"];
const PCT_STATS = ["rec_err_rate", "bb_rate", "k_rate", "xbh_gap_rate", "k_pct", "bb_pct", "ts_pct", "tp_pct", "ft_pct", "rim_pct", "mid_pct"];

const fmtValue = (stat, v) => {
  if (v == null) return "—";
  if (AVG_STATS.includes(stat)) return Number(v).toFixed(3).replace(/^0/, "");
  if (PCT_STATS.includes(stat)) return `${(v * 100).toFixed(1)}%`;
  if (stat === "ip") return Number(v).toFixed(1);
  return Number(v).toFixed(2);
};

export const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const PEERS = { volleyball: "at her position", softball: "among qualified D1 players", basketball: "among qualified D1 players" };
const RANKED = ["softball", "basketball"];

export const tagDetail = (t, sport = "volleyball") => {
  if (!t.stat) return sport === "softball" ? "Qualified as both a hitter and a pitcher in 2025" : t.label;
  return `${STAT_LABELS[t.stat] || t.stat.replace(/_/g, " ")}: ${fmtValue(t.stat, t.value)}${t.percentile != null ? ` — ${ordinal(t.percentile)} percentile ${PEERS[sport] || ""}` : ""}`;
};

export const AttrIcon = ({ file, sport, className = "h-5 w-5" }) => {
  const url = `url(/attribute-icons/${sport === "volleyball" ? "" : `${sport}/`}${file}) center / contain no-repeat`;
  return <span aria-hidden className={`inline-block shrink-0 bg-current ${className}`} style={{ WebkitMask: url, mask: url }} />;
};

const Chip = ({ t, sport }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`attribute-chip-${t.key}`} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          className="inline-flex items-center gap-2 rounded-full border border-[#FF2D78]/40 bg-[#FF2D78]/10 px-4 py-2 text-sm font-semibold text-[#FF2D78] transition-colors duration-200 hover:bg-[#FF2D78]/20">
          <AttrIcon file={t.icon} sport={sport} />
          <span className="text-white">{t.label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`attribute-detail-${t.key}`} side="top" className="w-auto max-w-xs border-slate-700 bg-[#0B0F19] px-3 py-2 font-data text-xs text-slate-200">
        {tagDetail(t, sport)}
      </PopoverContent>
    </Popover>
  );
};

const sortTags = (tags) => [...tags].sort((a, b) => (b.percentile ?? -1) - (a.percentile ?? -1));

const RankedChips = ({ tags, sport }) => {
  const [all, setAll] = useState(false);
  const sorted = sortTags(tags.filter((t) => t.percentile != null));
  const extra = tags.filter((t) => t.percentile == null);
  const shown = all ? sorted : sorted.slice(0, 4);
  const hidden = sorted.length - shown.length;
  return (
    <div data-testid="attributes-chips" className="flex flex-wrap items-center gap-3">
      {[...shown, ...extra].map((t) => <Chip key={t.key} t={t} sport={sport} />)}
      {hidden > 0 && (
        <button data-testid="attributes-more-button" onClick={() => setAll(true)}
          className="rounded-full border border-slate-700 px-4 py-2 font-data text-xs uppercase tracking-wider text-slate-300 transition-colors duration-200 hover:border-[#FF2D78] hover:text-white">
          +{hidden} more
        </button>
      )}
    </div>
  );
};

export const hasAttributes = (sport, attrs) =>
  sport === "volleyball" ? !!attrs?.qualified : RANKED.includes(sport) && !!attrs?.tags?.length;

export const AttributesPanel = ({ attrs, sport = "volleyball" }) => (
  <div className="space-y-4">
    {RANKED.includes(sport) ? <RankedChips tags={attrs.tags} sport={sport} /> : attrs.tags?.length ? (
      <div data-testid="attributes-chips" className="flex flex-wrap gap-3">{attrs.tags.map((t) => <Chip key={t.key} t={t} sport={sport} />)}</div>
    ) : (
      <p data-testid="attributes-none" className="text-sm text-slate-400">No standout attributes for 2025.</p>
    )}
    <p className="text-xs text-slate-500">
      {sport === "basketball"
        ? `Based on her 2025-26 season${attrs.school_2025_26 ? ` at ${attrs.school_2025_26}` : ""}${attrs.stats?.gp ? ` (${Math.round(attrs.stats.gp)} games, compared with qualified D1 players)` : ""}. Tap or hover a chip for the stat behind it.`
        : sport === "softball"
        ? `Based on her 2025 season${attrs.school_2025 ? ` at ${attrs.school_2025}` : ""}${attrs.games ? ` (${Math.round(attrs.games)} games, compared with qualified D1 players)` : ""}. Tap or hover a chip for the stat behind it.`
        : `Based on her 2025 season${attrs.school_2025 ? ` at ${attrs.school_2025}` : ""} (${attrs.sets_played} sets, compared with other ${attrs.position_group} players). Tap or hover a chip for the stat behind it.`}
    </p>
  </div>
);
